import { useMemo } from "react";
import { useValuesStore } from "@client/store/values";
import { useTicker } from "./useTicker";

export interface UseTopicResult {
  value: MQTTValue["value"] | undefined;
  topic: MQTTValue["topic"] | undefined;
  timestamp: Date | undefined;
  lastUpdated: number | undefined;
  suspicious: boolean;
  lastValueInterval: number | undefined;
  animationDuration: number | undefined;
}

const HARD_LIMIT_MS = 2 * 60 * 1000;

/**
 * Sleduje real-time hodnotu jednoho topicu.
 *
 * Refactor oproti původní implementaci:
 *  - Žádný `setTimeout(setStickyVal, 0)` hack — hodnota přichází přímo z Zustand storu (atomicky aktualizován).
 *  - Žádný per-hook `setInterval` — používá globální useTicker (1 timer pro celou app).
 *  - Selector pattern: re-render jen když se změní hodnota tohoto konkrétního topicu.
 */
export const useTopic = (topicId: string): UseTopicResult => {
  const value = useValuesStore((s) => s.byTopic.get(topicId));
  const tick = useTicker();

  return useMemo(() => {
    if (!value) {
      return {
        value: undefined,
        topic: undefined,
        timestamp: undefined,
        lastUpdated: undefined,
        suspicious: false,
        lastValueInterval: undefined,
        animationDuration: undefined,
      };
    }

    // Date.now() je tu OK - useMemo se přepočítá jen při změně value/tick (deterministic snapshot)
    // eslint-disable-next-line react-hooks/purity
    const now = Date.now();
    const ts = value.timestamp.getTime();
    const sinceLast = now - ts;

    // Interval mezi posledními dvěma hodnotami (z prev chain)
    let lastValueInterval: number | undefined;
    if (value.prev) {
      lastValueInterval = ts - value.prev.timestamp.getTime();
    }

    // Suspicious: pokud je hodnota starší než 2× očekávaný interval, nebo hard limit 2 min
    const allowedAge = lastValueInterval ? lastValueInterval * 2 : HARD_LIMIT_MS;
    const suspicious = sinceLast > allowedAge;

    // Animation duration pro Dot
    const animationDuration = lastValueInterval ? Math.max(0, ts + lastValueInterval - now) : undefined;

    return {
      value: value.value,
      topic: value.topic,
      timestamp: value.timestamp,
      lastUpdated: Math.round(sinceLast / 1000),
      suspicious,
      lastValueInterval,
      animationDuration,
    };
    // tick is intentionally a dependency to recompute every 500ms
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, tick]);
};
